import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PreviewCredentials {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
}

const CREDS_KEY_PREFIX = "dayone:preview:creds:";

/**
 * Hook to fetch and manage preview credentials for the sandbox iframe.
 * Credentials are fetched once per project and stored in localStorage
 * so the preview iframe can read them.
 */
export function usePreviewCredentials(projectId: string | null) {
  const [credentials, setCredentials] = useState<PreviewCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCredentials = useCallback(async () => {
    if (!projectId) return;

    // Check cache first
    const cacheKey = `${CREDS_KEY_PREFIX}${projectId}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Cache valid for 1 hour
        if (parsed.ts && Date.now() - parsed.ts < 3600000) {
          setCredentials(parsed.creds);
          return;
        }
      }
    } catch { /* ignore cache errors */ }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("project-env-vars", {
        body: { action: "get-preview-creds", projectId },
      });

      if (fnError) {
        console.error("[usePreviewCredentials] Error fetching credentials:", fnError);
        setError(fnError.message);
        return;
      }

      const creds = data?.creds as PreviewCredentials | null;
      setCredentials(creds);

      // Cache the result
      if (creds) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ creds, ts: Date.now() }));
        } catch { /* ignore cache errors */ }
      }

      // Also write to the preview credentials location so __render.tsx can read them
      if (creds?.VITE_SUPABASE_URL && creds?.VITE_SUPABASE_ANON_KEY) {
        try {
          localStorage.setItem("dayone:preview:supabaseUrl", creds.VITE_SUPABASE_URL);
          localStorage.setItem("dayone:preview:supabaseAnonKey", creds.VITE_SUPABASE_ANON_KEY);
          console.log("[usePreviewCredentials] Credentials injected for preview");
        } catch { /* ignore */ }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch credentials";
      console.error("[usePreviewCredentials] Error:", msg);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Fetch credentials when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchCredentials();
    } else {
      setCredentials(null);
    }
  }, [projectId, fetchCredentials]);

  // Clear credentials on unmount
  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem("dayone:preview:supabaseUrl");
        localStorage.removeItem("dayone:preview:supabaseAnonKey");
      } catch { /* ignore */ }
    };
  }, []);

  return {
    credentials,
    isLoading,
    error,
    refetch: fetchCredentials,
    hasCredentials: !!(credentials?.VITE_SUPABASE_URL && credentials?.VITE_SUPABASE_ANON_KEY),
  };
}

export default usePreviewCredentials;
